import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
/* One shared stylesheet covers all service pages */
import "./styles/Services.css";
import HeroBg from "../../assets/images/services/IT-Support-Hero.png";
import ItsupportHelpDesk from "../../assets/images/services/It support Help Desk Support.png";

const ITManagedSupportServices = () => {
  const navigate = useNavigate();

  const tabs = useMemo(
    () => [
      {
        key: "helpdesk",
        label: "Helpdesk Support",
        title: "Helpdesk Support",
        desc: "Our 24/7 helpdesk delivers on-demand IT support whenever you need it. We resolve issues fast so your team can stay productive and focused on the business.",
        cta: "Learn More",
        image: ItsupportHelpDesk,
      },
      {
        key: "endpoint",
        label: "Endpoint Management",
        title: "Endpoint Management",
        desc: "We manage, update, and secure your essential endpoints like desktops and laptops so your business stays protected, consistent, and running smoothly.",
        cta: "Learn More",
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80",
      },
      {
        key: "mdm",
        label: "Mobile Device Management",
        title: "Mobile Device Management",
        desc: "ALG Data Guard facilitates the management of your mobile devices, allowing your hybrid, remote, or BYOD model to operate seamlessly. We'll help you manage applications, improve security, and more to help you meet your business goals.",
        cta: "Learn More",
        image: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1400&q=80",
      },
    ],
    []
  );

  const [activeTab, setActiveTab] = useState(tabs[0]);

  /* ── Benefits reveal ── */
  const benefitsRef = useRef(null);
  const [benefitsInView, setBenefitsInView] = useState(false);
  useEffect(() => {
    const el = benefitsRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setBenefitsInView(true); },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* ── CTA reveal ── */
  const ctaRef = useRef(null);
  const [ctaInView, setCtaInView] = useState(false);
  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setCtaInView(true); obs.unobserve(el); }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="servicePage">

      {/* ── HERO ── */}
      <section className="hero-section">
        <div className="hero-section__grid-bg" aria-hidden="true" />

        <div className="hero-left">
          <div className="dd-serviceLabel">IT Managed Support</div>

          <h1 className="hc-hero__title">
            Always-on IT support,<br />
            zero downtime.
          </h1>

          <p className="hc-hero__lead">
            Get proactive, fully managed IT support with 24/7 monitoring, fast
            helpdesk response, endpoint management, and security-first maintenance
            to keep your team productive and your systems running smoothly.
          </p>

          <button
            className="btn-orange"
            type="button"
            onClick={() => navigate("/contact")}
          >
            Meet Our Experts
            <svg className="btn-arrow" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="hero-right">
          <div className="hero-image-container">
            <img className="hero-img" src={HeroBg} alt="IT support services team" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* ── SERVICES INTRO ── */}
      <section className="services-section">
        <div className="services-left">
          <p className="section-eyebrow">
            <span className="live-dot" aria-hidden="true" />
            Why ALG Data Guard
          </p>
          <h2>Enhance Your Business Operations With a Trusted IT Support Partner</h2>
          <p>
            Running a modern IT environment can feel overwhelming — especially as
            technology becomes more complex. From managing multiple devices to resolving
            day-to-day technical issues, you need dependable IT support that streamlines
            operations and gives your team more time to focus on what matters most: your business.
          </p>
          <p>
            Backed by years of experience delivering fully managed IT services, ALG Data Guard
            helps keep your organisation secure, connected, and productive. With a dedicated team
            and tailored managed support, you get guidance for long-term growth, smoother
            collaboration across teams, and stronger protection for your critical data.
          </p>
        </div>

        <div className="services-right">
          {[
            {
              label: "01",
              title: "Guide",
              body: "We act as your trusted partner, delivering value-driven IT solutions tailored to your business. Our team takes time to understand your goals so you can make smarter decisions and strengthen your IT infrastructure.",
            },
            {
              label: "02",
              title: "Connect",
              body: "ALG Data Guard provides smooth, reliable connectivity across your devices so your team can collaborate without friction — closing communication gaps and supporting more efficient teamwork.",
            },
            {
              label: "03",
              title: "Protect",
              body: "We put security first by implementing strong safeguards such as data backup and disaster recovery, along with proactive network monitoring to keep your critical information safe.",
            },
          ].map(({ label, title, body }) => (
            <div className="service-item" key={title}>
              <div className="service-icon-IT" aria-hidden="true">{label}</div>
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── MANAGED IT TABS ── */}
      <section className="managed-IT-section">
        <div className="managed-IT-header">
          <p className="section-eyebrow section-eyebrow--center"
            style={{ color: "rgba(255,255,255,0.45)", justifyContent: "center" }}>
            <span className="live-dot" aria-hidden="true" />
            Services
          </p>
          <h2>Complete IT Support for Your Business</h2>
          <p>
            ALG Data Guard delivers comprehensive technical IT support and customer
            service to keep your operations running smoothly and safely.
          </p>

          <div className="managed-tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={activeTab.key === t.key}
                className={`managed-tab${activeTab.key === t.key ? " active" : ""}`}
                onClick={() => setActiveTab(t)}
                type="button"
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="managed-IT-body tab-enter" key={activeTab.key}>
          <div className="managed-card">
            <div className="managed-imageWrap">
              <img
                className="managed-image"
                src={activeTab.image}
                alt={activeTab.title}
              />
            </div>

            <div className="managed-content">
              <h3>{activeTab.title}</h3>
              <p>{activeTab.desc}</p>
              <button
                className="managed-cta"
                type="button"
                onClick={() => navigate("/contact")}
              >
                {activeTab.cta} →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="IT-cta-section">
        <div
          ref={ctaRef}
          className={`IT-cta-card IT-cta-reveal${ctaInView ? " in" : ""}`}
        >
          <div className="IT-cta-left">
            <img
              className="IT-cta-illustration"
              src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80"
              alt="IT support team collaboration"
            />
          </div>

          <div className="IT-cta-right">
            <h2>
              Elevate Your Business With Robust{" "}
              <span>IT Managed Support Services</span>
            </h2>
            <p>
              From resolving day-to-day issues to improving and securing your endpoints,
              ALG Data Guard delivers managed IT support that helps your environment
              perform at its best. Contact us today to start building a smoother,
              more scalable IT foundation.
            </p>
            <button
              className="IT-cta-btn"
              type="button"
              onClick={() => navigate("/contact")}
            >
              Find My Support Solution →
            </button>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="IT-benefits-section">
        <div className="IT-benefits-wrap" ref={benefitsRef}>
          <div className={`benefits-left${benefitsInView ? " in" : ""}`}>
            <p className="section-eyebrow">
              <span className="live-dot" aria-hidden="true" />
              Coverage
            </p>
            <h2>Get High-Quality Outsourced IT Support You Can Rely On</h2>
            <div className="benefits-underline" />
            <p>
              We deliver expert outsourced IT support for businesses across North America.
              With teams based in Edmonton and additional locations in Toronto and Vancouver,
              we help both growing companies and established organisations run more efficiently.
              Through remote and onsite managed support, we provide the resources you need —
              wherever you operate.
            </p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ITManagedSupportServices;